import { useEffect, useRef } from "react";
import { MANAGED_SELECTORS, buildHead, siteUrl } from "@/lib/seoMeta";

/**
 * The single place page metadata is applied: <title>, description, canonical, robots, Open Graph and Twitter/X tags (+ JSON-LD).
 * Pages pass their data (usually spread from a builder in lib/seoMeta.js: <SEO {...productMeta(product)} />); the tag set itself
 * comes from buildHead() — the same function the build-time prerender uses to write the static HTML crawlers read.
 *
 * Props: title, description, keywords, image, imageAlt, path (canonical path; defaults to the current path), type, noindex, jsonLd.
 * (`url` is accepted as an alias of `path` for older callers.) Absolute URLs are always built on the PRODUCTION origin
 * (config/site.js, or REACT_APP_SITE_URL) — never window.location — so localhost/preview URLs cannot leak into shared metadata.
 *
 * Layering: several <SEO>s can be mounted at once (PublicLayout's site default, then the page's own). They form a stack; the most
 * recently mounted one wins and each re-render updates its own entry in place, so a late update to the default (e.g. settings
 * finishing loading) can never overwrite the page's tags. When the stack empties the original document head is restored.
 */
const BASE = siteUrl(process.env.REACT_APP_SITE_URL);
const SELECTOR = MANAGED_SELECTORS.join(",");

let original = null; // the head as first found (title + managed tags), restored when no <SEO> is mounted
const stack = [];
let nextId = 0;

function capture() {
  if (original) return;
  original = { title: document.title, tags: [...document.head.querySelectorAll(SELECTOR)].map((el) => el.cloneNode(true)) };
}

function paint() {
  document.head.querySelectorAll(SELECTOR).forEach((el) => el.remove());
  document.getElementById("seo-jsonld")?.remove();
  const top = stack[stack.length - 1];
  if (!top) {
    if (original) { original.tags.forEach((t) => document.head.appendChild(t.cloneNode(true))); document.title = original.title; }
    return;
  }
  document.title = top.head.title;
  top.head.tags.forEach((t) => {
    const el = document.createElement(t.tag);
    if (t.tag === "link") { el.rel = t.rel; el.href = t.href; } else { el.setAttribute(t.attr, t.name); el.setAttribute("content", t.content); }
    document.head.appendChild(el);
  });
  if (top.jsonLd) {
    const s = document.createElement("script");
    s.type = "application/ld+json"; s.id = "seo-jsonld"; s.text = JSON.stringify(top.jsonLd);
    document.head.appendChild(s);
  }
}

export default function SEO({ title, description, keywords, image, imageAlt, ogCardSize, path, url, type = "website", noindex = false, jsonLd }) {
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = ++nextId;
  const canonicalPath = path || url || (typeof window !== "undefined" ? window.location.pathname : "/");

  useEffect(() => {
    capture();
    const entry = { id: idRef.current, head: buildHead({ title, description, keywords, image, imageAlt, ogCardSize, path: canonicalPath, type, noindex }, BASE), jsonLd };
    const i = stack.findIndex((e) => e.id === entry.id);
    if (i >= 0) stack[i] = entry; else stack.push(entry);
    paint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, keywords, image, imageAlt, ogCardSize, canonicalPath, type, noindex, JSON.stringify(jsonLd || null)]);

  useEffect(() => () => {
    const i = stack.findIndex((e) => e.id === idRef.current);
    if (i >= 0) stack.splice(i, 1);
    paint();
  }, []);

  return null;
}
